import { useMemo, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useContentToken, useSlotGroup, useGallery } from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
import { RichTextContent } from '@vibesites/runtime/react';
import type { PhotoGallery } from '@vibesites/schema';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { CTA_OUTLINE, CtaStyles, ctaOverPrimary } from './_cta';

// ============================================================================
// Smart Photo Selection
// ============================================================================

const PANEL_KEYWORDS: Record<string, string[]> = {
  modern: [
    'interior', 'apartment', 'unit', 'living', 'kitchen', 'bedroom',
    'home', 'modern', 'luxury', 'inside', 'hallway', 'dining',
  ],
  amenities: [
    'pool', 'gym', 'fitness', 'clubhouse', 'lounge', 'amenity', 'amenities',
    'recreation', 'courtyard', 'grill', 'spa', 'theater', 'rooftop',
  ],
  neighborhood: [
    'aerial', 'drone', 'skyline', 'neighborhood', 'exterior', 'bird',
    'view', 'surrounding', 'city', 'street', 'building', 'location',
  ],
};

function scorePhoto(photo: PhotoGallery, keywords: string[]): number {
  const searchText = [photo.title, photo.description, photo.alt_text]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (!searchText) return 0;
  return keywords.reduce((score, kw) => score + (searchText.includes(kw) ? 1 : 0), 0);
}

function findBestPhotoIndex(
  gallery: PhotoGallery[],
  panelKey: string,
  usedIndices: Set<number>,
  fallbackIndex: number,
): number {
  const keywords = PANEL_KEYWORDS[panelKey] || [];
  if (!gallery.length || !keywords.length) return fallbackIndex;

  let bestIndex = -1;
  let bestScore = 0;

  for (let i = 0; i < gallery.length; i++) {
    if (usedIndices.has(i)) continue;
    const score = scorePhoto(gallery[i], keywords);
    if (score > bestScore) {
      bestScore = score;
      bestIndex = i;
    }
  }

  if (bestIndex >= 0) return bestIndex;

  for (let i = fallbackIndex; i < gallery.length; i++) {
    if (!usedIndices.has(i)) return i;
  }
  for (let i = 0; i < fallbackIndex; i++) {
    if (!usedIndices.has(i)) return i;
  }

  return Math.min(fallbackIndex, Math.max(gallery.length - 1, 0));
}

function useSmartPhotoIndices(): [number, number, number] {
  const gallery = useGallery();
  return useMemo(() => {
    const used = new Set<number>();
    const idx1 = findBestPhotoIndex(gallery, 'modern', used, 1);
    used.add(idx1);
    const idx2 = findBestPhotoIndex(gallery, 'amenities', used, 3);
    used.add(idx2);
    const idx3 = findBestPhotoIndex(gallery, 'neighborhood', used, 5);
    return [idx1, idx2, idx3];
  }, [gallery]);
}

function makeImageSlotDef(label: string, index: number): SlotGroupDefinition {
  return {
    label,
    fields: {
      image: { label: 'Image', type: 'image', defaultField: `photo_gallery[${index}].image` },
      alt: { label: 'Alt Text', type: 'text', defaultField: `photo_gallery[${index}].alt_text` },
    },
    sourceBinding: {
      collection: 'photo_gallery',
      defaultIndex: index,
      fieldMappings: { image: 'image', alt: 'alt_text' },
    },
  };
}

// ============================================================================
// Static slot group definitions (for contentSchema — must be inline literals)
// ============================================================================

const panel1ImageDef: SlotGroupDefinition = {
  label: 'Modern Living Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 1, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const panel2ImageDef: SlotGroupDefinition = {
  label: 'Amenities Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 3, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const panel3ImageDef: SlotGroupDefinition = {
  label: 'Prime Location Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[5].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[5].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 5, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const panel1CtaDef: SlotGroupDefinition = {
  label: 'Modern Living CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/gallery' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'Browse Photos' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const panel2CtaDef: SlotGroupDefinition = {
  label: 'Amenities CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/amenities' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'View All Amenities' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const panel3CtaDef: SlotGroupDefinition = {
  label: 'Prime Location CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/neighborhood' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'See What\'s Nearby' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Component
// ============================================================================

type TabId = 'panel1' | 'panel2' | 'panel3';

export function CTASection() {
  const [activeTab, setActiveTab] = useState<TabId>('panel1');

  const [idx1, idx2, idx3] = useSmartPhotoIndices();

  const imgDef1 = useMemo(() => makeImageSlotDef('Modern Living Image', idx1), [idx1]);
  const imgDef2 = useMemo(() => makeImageSlotDef('Amenities Image', idx2), [idx2]);
  const imgDef3 = useMemo(() => makeImageSlotDef('Prime Location Image', idx3), [idx3]);

  const img1 = useSlotGroup('ctasection.panel1_image', imgDef1);
  const img2 = useSlotGroup('ctasection.panel2_image', imgDef2);
  const img3 = useSlotGroup('ctasection.panel3_image', imgDef3);

  const cta1 = useSlotGroup('ctasection.panel1_cta', panel1CtaDef);
  const cta2 = useSlotGroup('ctasection.panel2_cta', panel2CtaDef);
  const cta3 = useSlotGroup('ctasection.panel3_cta', panel3CtaDef);

  const label1 = useContentToken('ctasection.panel1_label');
  const label2 = useContentToken('ctasection.panel2_label');
  const label3 = useContentToken('ctasection.panel3_label');

  const amenity1 = useContentToken('ctasection.amenity1');
  const amenity2 = useContentToken('ctasection.amenity2');
  const amenity3 = useContentToken('ctasection.amenity3');
  const amenity4 = useContentToken('ctasection.amenity4');
  const amenity5 = useContentToken('ctasection.amenity5');
  const amenity6 = useContentToken('ctasection.amenity6');

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLDivElement>();

  const tabs: { id: TabId; label: string | null }[] = [
    { id: 'panel1', label: label1 },
    { id: 'panel2', label: label2 },
    { id: 'panel3', label: label3 },
  ];

  return (
    <section ref={sectionRef} className="reveal-fade-up w-full bg-[var(--vibe-color-primary)]" style={ctaOverPrimary}>
      <CtaStyles />

      {/* ── Tab Bar ── */}
      <div className="cta-tab-bar flex bg-[var(--vibe-color-primary)] px-4 sm:px-6 md:px-8 lg:px-12">
        {tabs.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={activeTab === id}
            onClick={() => setActiveTab(id)}
            className={`cta-tab-btn flex-1 relative py-6 px-4 font-heading font-normal tracking-widest transition-colors duration-300 text-sm md:text-base ${
              activeTab === id
                ? 'text-primary-foreground opacity-100'
                : 'text-primary-foreground opacity-60 hover:opacity-90'
            }`}
          >
            {label || (id === 'panel1' ? 'Modern Living' : id === 'panel2' ? 'Amenities' : 'Prime Location')}
            <span
              className={`absolute bottom-0 left-0 right-0 transition-all duration-300 bg-[var(--vibe-color-text-on-primary)] ${
                activeTab === id ? 'h-[3px] opacity-100' : 'h-[3px] opacity-40'
              }`}
            />
          </button>
        ))}
      </div>

      {/* ── Panel 1: Modern Living ── */}
      <div className={activeTab === 'panel1' ? 'flex flex-col md:flex-row min-h-[540px] px-4 sm:px-6 md:px-8 lg:px-12' : 'hidden'}>

        {/* Text */}
        <div className="flex-1 flex flex-col justify-center px-6 py-12 md:px-16 md:py-24 bg-[var(--vibe-color-primary)]">
          <EditableToken
            path="ctasection.panel1_label"
            defaultValue="Modern Living"
            as="p"
            className="font-body text-[11px] uppercase tracking-[0.25em] text-primary-foreground mb-4"
          >
            {label1}
          </EditableToken>

          <div className="w-10 h-px bg-[var(--vibe-color-text-on-primary)] mb-7" />

          <RichTextContent
            path="ctasection.panel1_heading"
            label="Modern Living Heading"
            defaultValue="<p>Designed For Modern Luxury</p>"
            as="h2"
            className="font-heading text-3xl md:text-[2.5rem] font-normal text-primary-foreground leading-[1.15] mb-6 max-w-sm"
          />

          <RichTextContent
            path="ctasection.panel1_body"
            label="Modern Living Body Text"
            defaultValue="<p>Thoughtfully designed residences in the heart of downtown — featuring premium finishes, open-concept layouts, and a community built around how you live today.</p>"
            as="div"
            className="font-body font-light text-[15px] text-primary-foreground opacity-70 leading-loose mb-10 max-w-sm"
          />

          <EditableSlotGroup groupPath="ctasection.panel1_cta" definition={panel1CtaDef}>
            <a
              href={cta1.url || '/gallery'}
              target={cta1._target || '_self'}
              className={`self-start ${CTA_OUTLINE}`}
            >
              {cta1.label || 'Browse Photos'}
            </a>
          </EditableSlotGroup>
        </div>

        {/* Image */}
        <div className="flex-1 relative min-h-[380px] md:min-h-0">
          <EditableSlotGroup groupPath="ctasection.panel1_image" definition={imgDef1}>
            {img1.image ? (
              <VibesitesImage
                url={img1.image}
                alt={img1.alt || ''}
                className="absolute inset-0 w-full h-full object-cover object-center"
              />
            ) : (
              <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
            )}
          </EditableSlotGroup>
        </div>
      </div>

      {/* ── Panel 2: Amenities ── */}
      <div className={activeTab === 'panel2' ? 'flex flex-col md:flex-row min-h-[540px] px-4 sm:px-6 md:px-8 lg:px-12' : 'hidden'}>

        {/* Text */}
        <div className="flex-1 flex flex-col justify-center px-6 py-12 md:px-16 md:py-24 bg-[var(--vibe-color-primary)]">
          <EditableToken
            path="ctasection.panel2_label"
            defaultValue="Amenities"
            as="p"
            className="font-body text-[11px] uppercase tracking-[0.25em] text-primary-foreground mb-4"
          >
            {label2}
          </EditableToken>

          <div className="w-10 h-px bg-[var(--vibe-color-text-on-primary)] mb-7" />

          <RichTextContent
            path="ctasection.panel2_heading"
            label="Amenities Heading"
            defaultValue="<p>Resort-Style Living, Every Day</p>"
            as="h2"
            className="font-heading text-3xl md:text-[2.5rem] font-normal text-primary-foreground leading-[1.15] mb-6 max-w-sm"
          />

          <RichTextContent
            path="ctasection.panel2_body"
            label="Amenities Body Text"
            defaultValue="<p>Every detail crafted for the way you live — from sunrise to sundown.</p>"
            as="div"
            className="font-body font-light text-[15px] text-primary-foreground opacity-70 leading-loose mb-7 max-w-sm"
          />

          {/* Amenity Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mb-10 max-w-sm">
            <div className="flex items-center gap-2">
              <span className="w-1 h-1 rounded-full bg-[var(--vibe-color-text-on-primary)] flex-shrink-0" />
              <EditableToken path="ctasection.amenity1" defaultValue="Resort-Style Pool" as="span" className="font-body text-[13px] text-primary-foreground leading-snug">{amenity1}</EditableToken>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-1 h-1 rounded-full bg-[var(--vibe-color-text-on-primary)] flex-shrink-0" />
              <EditableToken path="ctasection.amenity2" defaultValue="24/7 Fitness Center" as="span" className="font-body text-[13px] text-primary-foreground leading-snug">{amenity2}</EditableToken>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-1 h-1 rounded-full bg-[var(--vibe-color-text-on-primary)] flex-shrink-0" />
              <EditableToken path="ctasection.amenity3" defaultValue="Penthouse Lounge" as="span" className="font-body text-[13px] text-primary-foreground leading-snug">{amenity3}</EditableToken>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-1 h-1 rounded-full bg-[var(--vibe-color-text-on-primary)] flex-shrink-0" />
              <EditableToken path="ctasection.amenity4" defaultValue="Rooftop Terrace" as="span" className="font-body text-[13px] text-primary-foreground leading-snug">{amenity4}</EditableToken>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-1 h-1 rounded-full bg-[var(--vibe-color-text-on-primary)] flex-shrink-0" />
              <EditableToken path="ctasection.amenity5" defaultValue="Dog-Friendly Spaces" as="span" className="font-body text-[13px] text-primary-foreground leading-snug">{amenity5}</EditableToken>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-1 h-1 rounded-full bg-[var(--vibe-color-text-on-primary)] flex-shrink-0" />
              <EditableToken path="ctasection.amenity6" defaultValue="Concierge Services" as="span" className="font-body text-[13px] text-primary-foreground leading-snug">{amenity6}</EditableToken>
            </div>
          </div>

          <EditableSlotGroup groupPath="ctasection.panel2_cta" definition={panel2CtaDef}>
            <a
              href={cta2.url || '/amenities'}
              target={cta2._target || '_self'}
              className={`self-start ${CTA_OUTLINE}`}
            >
              {cta2.label || 'View All Amenities'}
            </a>
          </EditableSlotGroup>
        </div>

        {/* Image */}
        <div className="flex-1 relative min-h-[380px] md:min-h-0">
          <EditableSlotGroup groupPath="ctasection.panel2_image" definition={imgDef2}>
            {img2.image ? (
              <VibesitesImage
                url={img2.image}
                alt={img2.alt || ''}
                className="absolute inset-0 w-full h-full object-cover object-center"
              />
            ) : (
              <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
            )}
          </EditableSlotGroup>
        </div>
      </div>

      {/* ── Panel 3: Prime Location ── */}
      <div className={activeTab === 'panel3' ? 'flex flex-col md:flex-row min-h-[540px] px-4 sm:px-6 md:px-8 lg:px-12' : 'hidden'}>

        {/* Text */}
        <div className="flex-1 flex flex-col justify-center px-6 py-12 md:px-16 md:py-24 bg-[var(--vibe-color-primary)]">
          <EditableToken
            path="ctasection.panel3_label"
            defaultValue="Prime Location"
            as="p"
            className="font-body text-[11px] uppercase tracking-[0.25em] text-primary-foreground mb-4"
          >
            {label3}
          </EditableToken>

          <div className="w-10 h-px bg-[var(--vibe-color-text-on-primary)] mb-7" />

          <RichTextContent
            path="ctasection.panel3_heading"
            label="Prime Location Heading"
            defaultValue="<p>Choose To Live Above It All</p>"
            as="h2"
            className="font-heading text-3xl md:text-[2.5rem] font-normal text-primary-foreground leading-[1.15] mb-6 max-w-sm"
          />

          <RichTextContent
            path="ctasection.panel3_body"
            label="Prime Location Body Text"
            defaultValue="<p>Steps from parks, galleries, dining, and transit — live at the center of everything the city has to offer, without giving up the peace of home.</p>"
            as="div"
            className="font-body font-light text-[15px] text-primary-foreground opacity-70 leading-loose mb-10 max-w-sm"
          />

          <EditableSlotGroup groupPath="ctasection.panel3_cta" definition={panel3CtaDef}>
            <a
              href={cta3.url || '/neighborhood'}
              target={cta3._target || '_self'}
              className={`self-start ${CTA_OUTLINE}`}
            >
              {cta3.label || "See What's Nearby"}
            </a>
          </EditableSlotGroup>
        </div>

        {/* Image */}
        <div className="flex-1 relative min-h-[380px] md:min-h-0">
          <EditableSlotGroup groupPath="ctasection.panel3_image" definition={imgDef3}>
            {img3.image ? (
              <VibesitesImage
                url={img3.image}
                alt={img3.alt || ''}
                className="absolute inset-0 w-full h-full object-cover object-center"
              />
            ) : (
              <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
            )}
          </EditableSlotGroup>
        </div>
      </div>

    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'CTASection',
  displayName: 'CTA Section',
  tokens: {
    // Tab labels
    'ctasection.panel1_label': {
      label: 'Tab 1 Label',
      type: 'text',
      defaultValue: 'Modern Living',
    },
    'ctasection.panel2_label': {
      label: 'Tab 2 Label',
      type: 'text',
      defaultValue: 'Amenities',
    },
    'ctasection.panel3_label': {
      label: 'Tab 3 Label',
      type: 'text',
      defaultValue: 'Prime Location',
    },
    // Panel 1 — Modern Living
    'ctasection.panel1_heading': {
      label: 'Modern Living Heading',
      type: 'richtext',
      defaultValue: '<p>Designed For Modern Luxury</p>',
    },
    'ctasection.panel1_body': {
      label: 'Modern Living Body Text',
      type: 'richtext',
      defaultValue:
        '<p>Thoughtfully designed residences in the heart of downtown — featuring premium finishes, open-concept layouts, and a community built around how you live today.</p>',
    },
    // Panel 2 — Amenities
    'ctasection.panel2_heading': {
      label: 'Amenities Heading',
      type: 'richtext',
      defaultValue: '<p>Resort-Style Living, Every Day</p>',
    },
    'ctasection.panel2_body': {
      label: 'Amenities Body Text',
      type: 'richtext',
      defaultValue: '<p>Every detail crafted for the way you live — from sunrise to sundown.</p>',
    },
    'ctasection.amenity1': {
      label: 'Amenity 1',
      type: 'text',
      defaultValue: 'Resort-Style Pool',
    },
    'ctasection.amenity2': {
      label: 'Amenity 2',
      type: 'text',
      defaultValue: '24/7 Fitness Center',
    },
    'ctasection.amenity3': {
      label: 'Amenity 3',
      type: 'text',
      defaultValue: 'Penthouse Lounge',
    },
    'ctasection.amenity4': {
      label: 'Amenity 4',
      type: 'text',
      defaultValue: 'Rooftop Terrace',
    },
    'ctasection.amenity5': {
      label: 'Amenity 5',
      type: 'text',
      defaultValue: 'Dog-Friendly Spaces',
    },
    'ctasection.amenity6': {
      label: 'Amenity 6',
      type: 'text',
      defaultValue: 'Concierge Services',
    },
    // Panel 3 — Prime Location
    'ctasection.panel3_heading': {
      label: 'Prime Location Heading',
      type: 'richtext',
      defaultValue: '<p>Choose To Live Above It All</p>',
    },
    'ctasection.panel3_body': {
      label: 'Prime Location Body Text',
      type: 'richtext',
      defaultValue:
        '<p>Steps from parks, galleries, dining, and transit — live at the center of everything the city has to offer, without giving up the peace of home.</p>',
    },
  },
  slotGroups: {
    'ctasection.panel1_image': panel1ImageDef,
    'ctasection.panel1_cta': panel1CtaDef,
    'ctasection.panel2_image': panel2ImageDef,
    'ctasection.panel2_cta': panel2CtaDef,
    'ctasection.panel3_image': panel3ImageDef,
    'ctasection.panel3_cta': panel3CtaDef,
  },
};

(CTASection as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default CTASection;
